import {
  Box,
  Flex,
  HStack,
  InlineFormattedNumber,
  Popover,
  SideEnum,
  Text,
  useCurrenciesContext,
  usePopoverState,
  type BuyingPower,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';
import { useMemo } from 'react';
import { useBuyingPower } from '../../../../hooks';
import { useDeepDiveContext } from '../../../../providers/DeepDiveProvider';
import { BuyingPowerRow } from './styles';

interface BuyingPowerSummaryProps {
  security: Security;
  baseBuyingPower: Map<string, BuyingPower> | undefined;
}

const sumBuyingPower = (selectedMarketAccounts: string[], buyingPower: Map<string, BuyingPower> | undefined) => {
  let maxBuy = Big(0);
  let maxSell = Big(0);
  buyingPower?.forEach(bp => {
    if (selectedMarketAccounts.includes(bp.MarketAccount)) {
      maxBuy = maxBuy.plus(bp.getMaxBuy() || 0);
      maxSell = maxSell.plus(bp.getMaxSell() || 0);
    }
  });
  return { maxBuy, maxSell };
};

export function BuyingPowerSummary({ security, baseBuyingPower }: BuyingPowerSummaryProps) {
  const { selectedMarketAccounts } = useDeepDiveContext();

  const { buyingPowerByMarketAccount: quoteBuyingPower } = useBuyingPower({
    symbol: security?.Symbol ?? '',
    currency: security?.QuoteCurrency ?? '',
    threshold: '10s',
    tag: 'BuyingPowerSummary',
  });

  const { baseMaxBuy, baseMaxSell, quoteMaxBuy, quoteMaxSell } = useMemo(() => {
    const { maxBuy: baseMaxBuy, maxSell: baseMaxSell } = sumBuyingPower(selectedMarketAccounts, baseBuyingPower);
    const { maxBuy: quoteMaxBuy, maxSell: quoteMaxSell } = sumBuyingPower(selectedMarketAccounts, quoteBuyingPower);
    return { baseMaxBuy, baseMaxSell, quoteMaxBuy, quoteMaxSell };
  }, [baseBuyingPower, quoteBuyingPower, selectedMarketAccounts]);

  return (
    <HStack gap="spacing8">
      <BuyingPowerSide side={SideEnum.Buy} security={security} baseMax={baseMaxBuy} quoteMax={quoteMaxBuy} />
      <BuyingPowerSide side={SideEnum.Sell} security={security} baseMax={baseMaxSell} quoteMax={quoteMaxSell} />
    </HStack>
  );
}

function BuyingPowerSide({
  side,
  security,
  baseMax,
  quoteMax,
}: {
  side: SideEnum;
  security: Security;
  baseMax: Big;
  quoteMax: Big;
}) {
  const { currenciesBySymbol } = useCurrenciesContext();
  const { BaseCurrency, QuoteCurrency } = security;
  const baseCurrency = BaseCurrency ? currenciesBySymbol.get(BaseCurrency) : undefined;
  const quoteCurrency = currenciesBySymbol.get(QuoteCurrency);
  const isZero = baseMax.eq(0);

  const popover = usePopoverState({
    trigger: 'hover',
  });

  return (
    <Popover {...popover}>
      <BuyingPowerRow alignItems="start" background={side === SideEnum.Buy ? 'colors.green.mute' : 'colors.red.mute'}>
        <Text size="fontSizeTiny" color="colorTextSubtle" transform="uppercase">
          Max {side === SideEnum.Buy ? 'Buy' : 'Sell'}
        </Text>
        <Flex gap="spacing4">
          <Text weight="fontWeightMedium">
            {!isZero ? (
              <InlineFormattedNumber
                specification={security.PriceDisplaySpec}
                number={baseMax}
                currency={BaseCurrency}
                round
                increment={baseCurrency?.DefaultIncrement}
              />
            ) : (
              '-'
            )}
          </Text>
        </Flex>
      </BuyingPowerRow>
      <Box>
        {!isZero ? (
          <InlineFormattedNumber
            specification={security.PriceDisplaySpec}
            number={quoteMax}
            currency={QuoteCurrency}
            round
            increment={quoteCurrency?.DefaultIncrement}
          />
        ) : (
          '-'
        )}
      </Box>
    </Popover>
  );
}
